import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { popisZmeny, POLE_POPISKY, poleTon } from "../lib/ukolyHistorie.js";

// Sekce "Historie" v detailu úkolu — kdo a kdy posunul deadline, přehodil pilota atd.
// Vykresluje se JEN ownerovi; o to se stará prop `enabled` (rodič zná roli).
//
// PROČ vlastní komponenta: úplně stejná sekce je na Operations boardu i na
// trenérském boardu, jen s jiným endpointem. Znění vět je společné (lib/ukolyHistorie.js).
//
// Pozn. k hookům: všechny useState/useEffect/useCallback/useMemo jsou nahoře,
// jediný early return je až POD nimi (React #310).
const VYCHOZI_POCET = 6;

export default function UkolHistorie({ endpoint, enabled = true }) {
  const [radky, setRadky] = useState(null);   // null = ještě nenačteno
  const [chyba, setChyba] = useState("");
  const [nacitam, setNacitam] = useState(false);
  const [vseRozbaleno, setVseRozbaleno] = useState(false);

  const nacti = useCallback(async () => {
    if (!enabled || !endpoint) return;
    setNacitam(true);
    setChyba("");
    try {
      const r = await apiFetch(endpoint);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Načtení historie selhalo");
      setRadky(Array.isArray(j.historie) ? j.historie : []);
    } catch (e) {
      setChyba(e.message);
      setRadky([]);
    } finally {
      setNacitam(false);
    }
  }, [endpoint, enabled]);

  useEffect(() => { nacti(); }, [nacti]);

  const viditelne = useMemo(() => {
    const vse = radky || [];
    return vseRozbaleno ? vse : vse.slice(0, VYCHOZI_POCET);
  }, [radky, vseRozbaleno]);

  if (!enabled) return null;

  const celkem = (radky || []).length;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <History className="w-4 h-4 text-plum-500" />
        <span className="text-sm font-bold text-slate-800">Historie</span>
        {celkem > 0 && <span className="text-xs text-slate-400 tabular-nums">{celkem}</span>}
        {nacitam && <RefreshCw className="w-3.5 h-3.5 text-slate-300 animate-spin" />}
      </div>

      {chyba ? (
        <div className="text-xs text-rose-600">{chyba}</div>
      ) : radky === null ? (
        <div className="text-xs text-slate-400">Načítám…</div>
      ) : celkem === 0 ? (
        <div className="text-xs text-slate-400">Zatím žádná změna sledovaných polí (termín, pilot, sloupec, priorita, název).</div>
      ) : (
        <>
          <ol className="space-y-1.5">
            {viditelne.map((h) => (
              <li key={h.id} className="flex items-start gap-2">
                <span className={`mt-0.5 shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${poleTon(h.pole)}`}>
                  {POLE_POPISKY[h.pole] || h.pole}
                </span>
                <span className="text-xs text-slate-700 leading-relaxed break-words demo-sensitive">{popisZmeny(h)}</span>
              </li>
            ))}
          </ol>
          {celkem > VYCHOZI_POCET && (
            <button type="button" onClick={() => setVseRozbaleno((v) => !v)}
              className="mt-2 text-[11px] font-semibold text-plum-600 hover:text-plum-800">
              {vseRozbaleno ? "Zobrazit méně" : `Zobrazit celou historii (${celkem})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
